import React from 'react';
import {StyleSheet} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchControlDepartments} from '@/entities/attendance/api';
import {moveDeviceToDepartment} from '@/entities/device/api';
import {ApiError} from '@/shared/api';
import {Banner} from '@/shared/ui/banner';
import {Card} from '@/shared/ui/card';
import {ChoiceChips} from '@/shared/ui/segmented';
import {spacing} from '@/shared/ui/theme';

/**
 * В каком отделе стоит камера (или регистратор — тогда все его камеры).
 *
 * Отдел у камеры — то, что связывает её с местом: по нему просмотр группирует дерево
 * («По отделам») и карточка отдела показывает «камеры отдела». Камера — такая же
 * строка устройств, как терминал, и пишется тем же маршрутом с той же сверкой
 * (`moveDeviceToDepartment`).
 */
const styles = StyleSheet.create({
  card: {gap: spacing(3)},
});

export function CameraDepartmentPanel({
  companyId,
  sourceId,
  departmentId,
}: {
  companyId: string;
  /** Строка устройства источника — его `peer`. */
  sourceId: string;
  departmentId: string | null | undefined;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId),
  });
  const move = useMutation({
    mutationFn: (id: string) => moveDeviceToDepartment(companyId, sourceId, id),
    onSuccess: () => qc.invalidateQueries({queryKey: ['devices']}),
  });

  const options = [
    {value: '', label: t('deviceAdmin.noDepartment')},
    ...(departments.data?.departments ?? []).map(d => ({
      value: d.department_id,
      label: d.name,
    })),
  ];

  return (
    <Card padding={4} style={styles.card}>
      <ChoiceChips
        value={move.isPending ? move.variables ?? '' : departmentId ?? ''}
        onChange={id => move.mutate(id)}
        disabled={move.isPending}
        options={options}
        testID="camera-department"
      />
      {move.isError ? (
        <Banner
          tone="error"
          title={
            (move.error as Error)?.message === 'department_not_applied'
              ? t('deviceAdmin.departmentNotApplied')
              : move.error instanceof ApiError
              ? move.error.message
              : t('errors.generic')
          }
        />
      ) : null}
    </Card>
  );
}
